import { chipColours } from '../../lib/colour';
import { useTheme } from '../../theme/ThemeProvider';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import type { PendingChoice } from './useCardDrag';

/**
 * ⭐ A LANE IS NOT A STATUS. Dropping onto a collapsed `Development` when several of its statuses are
 * possible targets has no single answer, so the board asks instead of choosing — picking the first,
 * or the lowest `sort_order`, would be the UI deciding a workflow step on the user's behalf and would
 * be wrong silently.
 *
 * 🔴 SINCE PH-19 THIS LIST IS EVERY ACTIVE STATUS IN THE LANE, not only the suggested ones.
 * ⛔ The old note here read *"the chooser cannot offer a status the API has not already said is
 * reachable"*, and that was the defect: `status_transition` is advice, and `performTransition`
 * *"never consults it"*.
 * ⭐⭐ THE SUGGESTED ONES ARE STILL FIRST AND STILL MARKED. Order and a badge are how a suggestion
 * stays useful without becoming a rule; ⛔ nothing here is disabled on account of `suggested`.
 */
export function LaneTargetChooser({
  choice,
  busy,
  onChoose,
  onCancel,
}: {
  choice: PendingChoice;
  busy: boolean;
  onChoose: (toStatus: string) => void;
  onCancel: () => void;
}) {
  const vocab = useVocabulary();
  const { resolved } = useTheme();

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center" role="presentation">
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" onClick={onCancel} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Choose a status in ${choice.column.label}`}
        className="safe-bottom relative w-full max-w-sm rounded-t-xl border border-[color:var(--color-line)] bg-[color:var(--color-surface-raised)] p-4 shadow-2xl sm:rounded-xl"
      >
        <p className="text-sm font-semibold">
          Move {choice.ticket.key} into {choice.column.label}
        </p>
        <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
          {choice.targets.length} statuses in this lane, from{' '}
          {vocab.status(choice.ticket.status)?.label ?? choice.ticket.status}. Choose one — the lane
          itself is not a status.
          {choice.targets.some((target) => target.suggested) ? (
            <>
              {' '}
              The ones marked <strong>suggested</strong> are what usually comes next; the rest are
              equally valid.
            </>
          ) : null}
        </p>

        <ul className="mt-3 space-y-1">
          {choice.targets.map((target) => {
            const colours = chipColours(target.colour ?? vocab.status(target.to_status)?.colour, resolved === 'dark');
            return (
              <li key={target.to_status}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onChoose(target.to_status)}
                  className="flex w-full items-center gap-2 rounded px-2 py-2 text-left hover:bg-black/5 disabled:opacity-60 dark:hover:bg-white/10"
                >
                  <span
                    style={{ backgroundColor: colours.background, color: colours.foreground }}
                    className="rounded-[3px] px-1.5 py-0.5 text-[11px] font-bold tracking-wide uppercase"
                  >
                    {target.label ?? vocab.status(target.to_status)?.label ?? target.to_status}
                  </span>
                  {/* ⭐ A hint, never a gate — the button beside it is enabled either way. */}
                  {target.suggested ? (
                    <span className="rounded-full border border-current/30 px-1.5 py-0.5 text-[10px] font-semibold text-[color:var(--color-ink-muted)]">
                      suggested
                    </span>
                  ) : null}
                  {target.note ? (
                    <span className="truncate text-xs text-[color:var(--color-ink-muted)]">
                      {target.note}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          onClick={onCancel}
          className="mt-3 h-9 w-full rounded border border-[color:var(--color-line)] text-sm font-medium"
        >
          Cancel — leave it where it is
        </button>
      </div>
    </div>
  );
}
